import { ReactNode, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import LoginBgImg from '@/assets/loginBg.png';
import LoginLogoImg from '@/assets/loginLogo.png';
import {
  FormattedMessage,
  MessageDescriptor,
  useIntl,
} from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { useAuthContext } from '@/contexts/AuthContext';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';
import { fetchInvitationStatus } from '@/lib/api';
import { getAuth0ApiAuthorizationParams } from '@/constants/auth0Api';
import {
  clearLoginRejection,
  isLoginRejectionReason,
  readLoginRejection,
} from '@/lib/loginRejection';

const LoginPage = () => {
  const { loginWithRedirect } = useAuth0();
  const { user, isPending: isAuthPending } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasConsented, acceptAll } = useCookieConsent();
  const [showCookieModal, setShowCookieModal] = useState(false);

  // A refused sign-in arrives as `?reason=` while the app is still on its
  // way out to Auth0's logout (AuthContext navigates here first), and only
  // in storage once back, since logout must return to the plain /login.
  // Storage is read once, and only in the second case: clearing it before
  // the logout round trip would lose it.
  const queryReason = searchParams.get('reason');
  const hasQueryReason = isLoginRejectionReason(queryReason);
  const [storedRejection] = useState(() =>
    hasQueryReason ? undefined : readLoginRejection(),
  );
  useEffect(() => {
    if (storedRejection) clearLoginRejection();
  }, [storedRejection]);
  const rejectionReason = hasQueryReason
    ? queryReason
    : storedRejection?.reason;

  // An Auth0 Organizations invitation email links here. Auth0 silently
  // falls back to the plain login page for an expired or revoked ticket,
  // so the backend is asked first.
  const invitation =
    searchParams.get('invitation') ?? storedRejection?.invitation;
  const organization =
    searchParams.get('organization') ?? storedRejection?.organization;
  const hasInvitation = !!invitation && !!organization;
  // A refusal on an invitation link offers to accept the invitation,
  // which restarts the invitation flow below.
  const [isAcceptingInvitation, setIsAcceptingInvitation] =
    useState(false);
  const showRejection = !!rejectionReason && !isAcceptingInvitation;
  const isInvitation = !showRejection && hasInvitation;
  // Someone already signed in who opens an invitation link just goes on
  // to the app; there is nothing for them to accept.
  const isSignedInInvitee = isInvitation && !isAuthPending && !!user;

  const {
    data: invitationStatus,
    isError: isInvitationStatusError,
    isFetching: isInvitationStatusFetching,
    refetch: refetchInvitationStatus,
  } = useQuery({
    queryKey: ['invitationStatus', invitation],
    queryFn: () => fetchInvitationStatus(invitation as string),
    enabled: isInvitation && !isAuthPending && !user,
    retry: false,
  });
  const isValidInvitation =
    isInvitation && invitationStatus?.valid === true;

  // Logs in straight away unless the page has something to tell the user
  // first: a refused sign-in, or an invitation that is still being
  // checked, is no longer valid, or could not be checked.
  const canLoginAutomatically = isInvitation
    ? isValidInvitation
    : !showRejection;

  const login = () => {
    if (isValidInvitation) {
      loginWithRedirect({
        authorizationParams: {
          ...getAuth0ApiAuthorizationParams(),
          invitation: invitation as string,
          organization: organization as string,
          screen_hint: 'signup',
        },
      });
    } else {
      loginWithRedirect();
    }
  };

  const handleLogin = () => {
    if (!hasConsented) {
      setShowCookieModal(true);
    } else {
      login();
    }
  };

  const handleAcceptAndLogin = () => {
    acceptAll();
    setShowCookieModal(false);
    login();
  };

  useEffect(() => {
    if (isSignedInInvitee) {
      navigate('/', { replace: true });
    }
  }, [isSignedInInvitee, navigate]);

  useEffect(() => {
    if (hasConsented && canLoginAutomatically) {
      login();
    }
    // `login` is rebuilt every render; this must only fire when the page
    // becomes ready to log in, not on every render after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConsented, canLoginAutomatically]);

  const renderMessage = (message: ReactNode, actions: ReactNode) => (
    <LoginModal>
      <LoginModalHeader />
      <ModalBody>
        <Message role="alert">{message}</Message>
        <Actions>{actions}</Actions>
      </ModalBody>
    </LoginModal>
  );

  // The user goes back to Auth0 from a message only on request, so an SSO
  // session (e.g. Google) cannot loop them straight back here.
  const renderRejection = (message: ReactNode) =>
    renderMessage(
      message,
      <>
        {hasInvitation && (
          <LoginActionButton
            variant="outlined"
            onClick={() => setIsAcceptingInvitation(true)}
            message={{
              id: 'acceptInvitation',
              defaultMessage: 'Accept invitation',
            }}
          />
        )}
        <LoginActionButton
          onClick={handleLogin}
          message={{
            id: 'backToLogin',
            defaultMessage: 'Back to login',
          }}
        />
      </>,
    );

  const goToLoginButton = (
    <LoginActionButton
      onClick={handleLogin}
      message={{
        id: 'goToLogin',
        defaultMessage: 'Go to login',
      }}
    />
  );

  const renderContent = () => {
    if (showRejection && rejectionReason === 'not_invited') {
      return renderRejection(
        <FormattedMessage
          id="login.notInvited"
          defaultMessage="This account has not been invited to RubyPlay Partner Portal. Ask your administrator for an invitation."
        />,
      );
    }
    if (showRejection && rejectionReason === 'deactivated') {
      return renderRejection(
        <FormattedMessage
          id="login.deactivated"
          defaultMessage="Your account has been deactivated. Contact your administrator."
        />,
      );
    }
    if (isInvitation) {
      if (isInvitationStatusError && !isInvitationStatusFetching) {
        return renderMessage(
          <FormattedMessage
            id="invitation.checkFailed"
            defaultMessage="We couldn't check your invitation. Please try again."
          />,
          <>
            <LoginActionButton
              variant="outlined"
              onClick={() => void refetchInvitationStatus()}
              message={{
                id: 'tryAgain',
                defaultMessage: 'Try again',
              }}
            />
            {goToLoginButton}
          </>,
        );
      }
      if (
        invitationStatus?.valid === false &&
        !isInvitationStatusFetching
      ) {
        return renderMessage(
          <FormattedMessage
            id="invitation.expired"
            defaultMessage="This invitation has expired, ask your administrator to resend it"
          />,
          goToLoginButton,
        );
      }
      // Still checking, or valid and about to leave for Auth0.
      if (!isValidInvitation || hasConsented) {
        return (
          <LoaderWrapper>
            <CircularProgress aria-label="Loading" />
          </LoaderWrapper>
        );
      }
    }
    if (hasConsented) {
      return null;
    }
    return (
      <LoginModal>
        <LoginModalHeader />
        <ModalBody>
          <StyledButton
            variant="contained"
            fullWidth
            type="submit"
            onClick={handleLogin}
            aria-label="Continue Button"
          >
            <FormattedMessage id="continue" defaultMessage="Continue" />
          </StyledButton>
        </ModalBody>
      </LoginModal>
    );
  };

  return (
    <>
      <Root>{renderContent()}</Root>
      <Modal
        open={showCookieModal}
        onClose={() => setShowCookieModal(false)}
        title={
          <FormattedMessage
            id="cookieConsent.loginRequired.title"
            defaultMessage="We value your privacy"
          />
        }
        actions={[
          <Button
            key="cancel"
            variant="text"
            onClick={() => setShowCookieModal(false)}
            aria-label="Cancel Button"
          >
            <FormattedMessage
              id="cookieConsent.cancel"
              defaultMessage="Cancel"
            />
          </Button>,
          <Button
            key="accept"
            variant="contained"
            onClick={handleAcceptAndLogin}
            aria-label="Accept Button"
          >
            <FormattedMessage
              id="cookieConsent.loginRequired.accept"
              defaultMessage="Accept"
            />
          </Button>,
        ]}
      >
        <FormattedMessage
          id="cookieConsent.loginRequired.description"
          defaultMessage="To sign in to your account, we need to use cookies for authentication, security, and to improve your experience."
        />
      </Modal>
    </>
  );
};

const LoginActionButton = ({
  message,
  onClick,
  variant = 'contained',
}: {
  message: MessageDescriptor;
  onClick: () => void;
  variant?: 'contained' | 'outlined';
}) => {
  const intl = useIntl();
  const label = intl.formatMessage(message);
  return (
    <StyledButton
      variant={variant}
      fullWidth
      onClick={onClick}
      aria-label={label}
    >
      {label}
    </StyledButton>
  );
};

const LoginModalHeader = () => (
  <ModalHeader>
    <Logo src={LoginLogoImg} alt="Partner Portal Logo" />
    <Header>
      <FormattedMessage
        id="signInHeader"
        defaultMessage="Partner Sign In"
      />
    </Header>
    <Subheader>
      <FormattedMessage
        id="signInSubheader"
        defaultMessage="To continue to the Partner Portal"
      />
    </Subheader>
  </ModalHeader>
);

const LoaderWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
});

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  background: `url(${LoginBgImg}) no-repeat center center`,
  backgroundSize: 'cover',
});

const LoginModal = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '540px',
  left: '50%',
  color: theme.palette.textPrimaryWhite,
  transform: 'translateX(-50%)',
  top: '20%',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0px 4px 10px 0px #00000066',
}));

const ModalHeader = styled('div')({
  background: '#E6162680',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '25px 0 26px',
});

const Logo = styled('img')({
  width: 90,
});

const Header = styled('div')({
  fontSize: 24,
  lineHeight: '28px',
  fontWeight: 600,
  marginTop: 9,
});

const Subheader = styled('div')({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  marginTop: 5,
});

const ModalBody = styled('div')({
  background: '#fff',
  padding: '42px 95px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const Message = styled('div')(({ theme }) => ({
  color: theme.palette.textPrimaryBlack,
  fontSize: 16,
  lineHeight: '24px',
  textAlign: 'center',
  marginBottom: 30,
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%',
}));

const StyledButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  lineHeight: '20px',
  padding: '14px 0',
  borderRadius: 8,
});

export default LoginPage;
